import { useStore } from '@nanostores/react';
import { $turnQueue, leaveQueue } from '~/lib/stores/turn-queue';

/**
 * The build waiting room.
 *
 * Shown when every turn slot is busy and the request has been parked rather
 * than failed. The wait is the same either way; what changes is that the person
 * can see it is a queue, see it moving, and see roughly how long. An error
 * message for the same delay reads as a broken product.
 *
 * The numbers here update over the websocket (with a poll fallback) — the user
 * never has to refresh, and refreshing is safe because the place in line lives
 * on the server, not in this component.
 */

/** Round an ETA to something a person would actually say out loud. Precision
 *  we don't have ("2m 47s") reads as a promise; a rounded range reads as an
 *  estimate, which is what it is. */
function formatEta(sec: number): string {
  if (sec <= 0) {
    return 'any moment';
  }

  if (sec < 60) {
    return 'under a minute';
  }

  const minutes = Math.round(sec / 60);

  return `about ${minutes} minute${minutes === 1 ? '' : 's'}`;
}

export function BuildQueueBanner() {
  const queue = useStore($turnQueue);

  if (!queue.chatId) {
    return null;
  }

  /*
   * Deliberately no `starting` state to render: admitFromQueue latches and
   * clears it synchronously, so React never observes it. It is a re-entrancy
   * guard in the store, not a UI phase — the build's own streaming UI is what
   * takes over here.
   */
  return (
    <div className="ui-banner-idle w-full">
      <span className="i-svg-spinners:90-ring-with-bg text-accent text-[14px] flex-shrink-0" />
      <span className="flex-1 min-w-0">
        {queue.position === 1 ? (
          <>You&apos;re next in line — starting {formatEta(queue.etaSec)}.</>
        ) : (
          <>
            You&apos;re <strong>#{queue.position}</strong> of {queue.total} waiting to build — {formatEta(queue.etaSec)}
            .
          </>
        )}{' '}
        <span className="opacity-70">We&apos;ll start automatically, no need to refresh.</span>
      </span>
      <button
        type="button"
        onClick={() => leaveQueue()}
        className="ui-btn-ghost flex items-center gap-1"
        title="Leave the queue — your message stays, you can send it again later"
      >
        <span className="i-ph:x text-[12px]" />
        Cancel
      </button>
    </div>
  );
}
